import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { useCustomerTags } from "@/lib/customer-tags";

/**
 * Two-up scope pickers for customer custom fields:
 *   - Tags: multi-select fed by the tenant's customer-tag taxonomy.
 *   - Clients: searchable multi-select, typeahead over `/api/v1/customers?q=`.
 *
 * Selected items render as chips above each picker. Empty on both
 * sides = no scope = field applies to every client.
 */
export function CustomerScopePickers({
  tagIds,
  clientIds,
  onTagIdsChange,
  onClientIdsChange,
  onManageTags,
}: {
  tagIds: string[];
  clientIds: string[];
  onTagIdsChange: (next: string[]) => void;
  onClientIdsChange: (next: string[]) => void;
  /** Optional CTA next to Tags picker that opens the tag manager. */
  onManageTags?: () => void;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <TagPicker
        value={tagIds}
        onChange={onTagIdsChange}
        onManageTags={onManageTags}
      />
      <ClientPicker value={clientIds} onChange={onClientIdsChange} />
    </div>
  );
}

function TagPicker({
  value,
  onChange,
  onManageTags,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  onManageTags?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const q = useCustomerTags();
  const tags = q.data?.tags ?? [];
  const byId = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);
  const selected = new Set(value);

  const toggle = (tagId: string) => {
    if (selected.has(tagId)) onChange(value.filter((id) => id !== tagId));
    else onChange([...value, tagId]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Apply to tags
        </p>
        {onManageTags && (
          <button
            type="button"
            onClick={onManageTags}
            className="text-xs text-primary hover:underline"
          >
            Manage tags
          </button>
        )}
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const tag = byId.get(id);
            if (!tag) return null;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs"
              >
                {tag.name}
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  aria-label={`Remove ${tag.name}`}
                  className="hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            type="button"
            className="w-full justify-between"
          >
            <span className="text-muted-foreground">
              {value.length === 0 ? "Pick tags…" : `${value.length} selected`}
            </span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search tags…" />
            <CommandList>
              <CommandEmpty>No tags yet. Click Manage tags to add some.</CommandEmpty>
              <CommandGroup>
                {tags.map((t) => {
                  const isOn = selected.has(t.id);
                  return (
                    <CommandItem
                      key={t.id}
                      value={t.name}
                      onSelect={() => toggle(t.id)}
                    >
                      <Check
                        className={`mr-2 h-3.5 w-3.5 ${
                          isOn ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <span>{t.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

type ClientLite = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
};

function fullName(c: ClientLite) {
  return (
    [c.firstName, c.lastName].filter(Boolean).join(" ") ||
    c.email ||
    "(unnamed)"
  );
}

function ClientPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const accessToken = useAuthStore((s) => s.accessToken);

  // Search-on-type. When the popover is closed we don't fetch, but we
  // still need to resolve the chip labels for already-selected clients,
  // so we keep a separate query for the picked ids.
  const search = useQuery({
    queryKey: ["customers.scope.search", q],
    enabled: !!accessToken && open,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/customers", {
          params: { query: { q: q || undefined, page: 0, size: 30 } },
        }),
      ),
  });

  // Hydrate chip labels for the saved value. We re-use the search results
  // when they happen to include the picked ids; otherwise we fall back
  // to a one-shot detail fetch keyed by id-set so chips don't render
  // as "(unknown)" before the operator has typed anything.
  const picked = useQuery({
    queryKey: ["customers.scope.picked", value.slice().sort().join(",")],
    enabled: !!accessToken && value.length > 0,
    queryFn: async () => {
      const out: Record<string, ClientLite> = {};
      for (const id of value) {
        try {
          const detail = unwrap(
            await api.GET("/api/v1/customers/{id}", {
              params: { path: { id } },
            }),
          );
          out[id] = {
            id,
            firstName: (detail as { firstName?: string }).firstName ?? null,
            lastName: (detail as { lastName?: string }).lastName ?? null,
            email: (detail as { email?: string }).email ?? null,
          };
        } catch {
          // Customer was deleted or cross-tenant — drop the chip on next save.
        }
      }
      return out;
    },
  });

  const results: ClientLite[] =
    ((search.data as { content?: ClientLite[] } | undefined)?.content ?? []);
  const labelById = picked.data ?? {};
  const selected = new Set(value);

  const toggle = (id: string) => {
    if (selected.has(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Apply to specific clients
      </p>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const c = labelById[id];
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full bg-muted text-foreground px-2 py-0.5 text-xs"
              >
                {c ? fullName(c) : "Loading…"}
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  aria-label={`Remove client ${id}`}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            type="button"
            className="w-full justify-between"
          >
            <span className="text-muted-foreground">
              {value.length === 0
                ? "Pick clients…"
                : `${value.length} selected`}
            </span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search name, email, or phone…"
              value={q}
              onValueChange={setQ}
            />
            <CommandList>
              {search.isLoading && (
                <div className="px-3 py-4 text-xs text-muted-foreground">
                  Searching…
                </div>
              )}
              {!search.isLoading && results.length === 0 && (
                <CommandEmpty>No matches.</CommandEmpty>
              )}
              <CommandGroup>
                {results.map((c) => {
                  const isOn = selected.has(c.id);
                  return (
                    <CommandItem
                      key={c.id}
                      value={c.id}
                      onSelect={() => toggle(c.id)}
                    >
                      <Check
                        className={`mr-2 h-3.5 w-3.5 ${
                          isOn ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <div className="min-w-0">
                        <p className="truncate">{fullName(c)}</p>
                        {c.email && (
                          <p className="text-[11px] text-muted-foreground truncate">
                            {c.email}
                          </p>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
